import { useMemo, useRef, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatCurrency } from '../../utils/formatters.js';

/**
 * Searchable product combobox: type to filter by SKU or name, pick with
 * mouse or ArrowUp/Down + Enter. Replaces the unsearchable native <select>
 * in line-item forms. `valueLabel` is the fallback display text when the
 * selected product is not (yet) in `products` - e.g. right after inline
 * creation, before the react-query refetch lands.
 */
export function ProductPicker({ products, value, valueLabel, onPick, onCreateNew, placeholder }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const selected = (products || []).find((p) => p.id === value);
  const selectedLabel = selected ? `${selected.sku} - ${selected.name}` : (value ? valueLabel || '' : '');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = products || [];
    const hits = q
      ? all.filter((p) => p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q))
      : all;
    return hits.slice(0, 50);
  }, [products, query]);

  function openList() {
    setQuery('');
    setHighlight(0);
    setOpen(true);
  }

  function pick(product) {
    onPick(product.id);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      openList();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[highlight]) pick(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="combobox">
      <input
        ref={inputRef}
        className="input"
        role="combobox"
        aria-expanded={open}
        value={open ? query : selectedLabel}
        placeholder={placeholder || t('picker.product.placeholder')}
        onFocus={openList}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
      />
      {open && (
        <div className="combobox-list" role="listbox">
          {matches.length === 0 && <div className="combobox-empty">{t('picker.product.empty')}</div>}
          {matches.map((p, i) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={p.id === value}
              className={`combobox-option${i === highlight ? ' is-active' : ''}`}
              // mousedown (not click) so the pick lands before the input's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span className="combobox-option-sku mono">{p.sku}</span>
              <span className="combobox-option-name">{p.name}</span>
              <span className="combobox-option-price mono">{formatCurrency(p.listPrice)}</span>
            </button>
          ))}
          {onCreateNew && (
            <button
              type="button"
              className="combobox-create"
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
                onCreateNew(query.trim());
              }}
            >
              + {t('picker.product.create')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
